<template>
  <main
    v-if="displayCode !== 403"
    class="error-page"
    :data-tone="theme.tone"
  >
    <div class="error-page__frame">
      <header class="error-page__header">
        <button
          type="button"
          class="error-page__brand"
          :aria-label="`${t('clever')} ${t('docs')}`"
          @click="goHome"
        >
          <span>{{ t('clever') }}</span
          ><strong>{{ t('docs') }}</strong>
        </button>
      </header>

      <AtomsLanguagePillSwitcher />

      <section class="error-page__content">
        <div class="error-page__copy">
          <div class="error-page__badge">
            <i :class="theme.icon" aria-hidden="true"></i>
            <span>{{ t('errorPages.eyebrow', { code: displayCode }) }}</span>
          </div>

          <div class="error-page__code" aria-hidden="true">
            {{ displayCode }}<span>.</span>
          </div>

          <h1>{{ errorTitle }}</h1>
          <p class="error-page__description">{{ errorDescription }}</p>

          <div class="error-page__actions">
            <button
              type="button"
              class="error-page__button is-primary"
              @click="goHome"
            >
              <i class="icon-home" aria-hidden="true"></i>
              <span>{{ t('errorPages.goHome') }}</span>
            </button>

            <button type="button" class="error-page__button" @click="tryAgain">
              <i class="icon-refresh" aria-hidden="true"></i>
              <span>{{ t('errorPages.tryAgain') }}</span>
            </button>

            <button
              type="button"
              class="error-page__button is-quiet"
              @click="goBack"
            >
              <i
                class="icon-arrow-back"
                :class="{ 'is-rtl': locale === 'ar' }"
                aria-hidden="true"
              ></i>
              <span>{{ t('errorPages.goBack') }}</span>
            </button>
          </div>
        </div>

        <aside
          class="error-page__panel"
          :aria-label="t('errorPages.panelTitle')"
        >
          <div class="error-page__visual" aria-hidden="true">
            <div class="error-page__sheet is-back"></div>
            <div class="error-page__sheet is-middle"></div>
            <div class="error-page__sheet is-front">
              <span class="error-page__sheet-line is-short"></span>
              <span class="error-page__sheet-line"></span>
              <span class="error-page__sheet-line"></span>
              <span class="error-page__sheet-line is-short"></span>
            </div>
            <div class="error-page__visual-mark">
              <i :class="theme.icon"></i>
            </div>
            <span class="error-page__visual-code">{{ displayCode }}</span>
          </div>

          <div class="error-page__panel-heading">
            <span>{{ t('errorPages.statusLabel') }}</span>
            <strong>{{ t('errorPages.panelTitle') }}</strong>
          </div>

          <dl class="error-page__facts">
            <div>
              <dt>{{ t('errorPages.whatHappened') }}</dt>
              <dd>{{ whatHappened }}</dd>
            </div>
            <div>
              <dt>{{ t('errorPages.nextStep') }}</dt>
              <dd>{{ nextStep }}</dd>
            </div>
          </dl>

          <p class="error-page__support">
            <i class="icon-alert-circle" aria-hidden="true"></i>
            <span>{{
              t('errorPages.supportHint', { code: displayCode })
            }}</span>
          </p>
        </aside>
      </section>
    </div>
  </main>
</template>

<script setup lang="ts">
import type { ErrorPageCode } from '~/utils/errorPages';
import { normalizeErrorPageCode } from '~/utils/errorPages';

interface ErrorTheme {
  icon: string;
  tone: 'blue' | 'orange' | 'red';
}

const props = withDefaults(
  defineProps<{
    code?: number | string;
    recoverFromNuxtError?: boolean;
  }>(),
  {
    code: 500,
    recoverFromNuxtError: false,
  },
);

const themes: Record<ErrorPageCode, ErrorTheme> = {
  400: { icon: 'icon-alert-circle', tone: 'blue' },
  401: { icon: 'icon-lock', tone: 'orange' },
  403: { icon: 'icon-shield-lock', tone: 'red' },
  404: { icon: 'icon-route-off', tone: 'blue' },
  408: { icon: 'icon-clock', tone: 'orange' },
  409: { icon: 'icon-file-alert', tone: 'red' },
  422: { icon: 'icon-file-alert', tone: 'orange' },
  429: { icon: 'icon-hourglass', tone: 'red' },
  500: { icon: 'icon-server', tone: 'red' },
  503: { icon: 'icon-database-off', tone: 'orange' },
};

const { t, locale } = useI18n();
const localePath = useLocalePath();
const router = useRouter();
const toast = useToast();

const displayCode = computed(() => normalizeErrorPageCode(props.code));
const theme = computed(() => themes[displayCode.value]);
const translationRoot = computed(() => `errorPages.codes.${displayCode.value}`);
const errorTitle = computed(() => t(`${translationRoot.value}.title`));
const errorDescription = computed(() =>
  t(`${translationRoot.value}.description`),
);
const whatHappened = computed(() => t(`${translationRoot.value}.whatHappened`));
const nextStep = computed(() => t(`${translationRoot.value}.nextStep`));

useHead(() => ({
  title: `${displayCode.value} | ${errorTitle.value}`,
  htmlAttrs: {
    dir: locale.value === 'ar' ? 'rtl' : 'ltr',
    lang: locale.value,
  },
}));

onMounted(() => {
  if (displayCode.value !== 403) return;

  if (!props.recoverFromNuxtError) {
    toast.add({
      title: t('youDontHavePermissionForPage'),
      color: 'red',
    });
  }

  void goHome();
});

async function goHome() {
  const destination = localePath('/');

  if (props.recoverFromNuxtError) {
    await clearError({ redirect: destination });
    return;
  }

  await navigateTo(destination);
}

async function tryAgain() {
  if (props.recoverFromNuxtError) {
    await clearError();
  }

  if (import.meta.client) {
    window.location.reload();
  }
}

async function goBack() {
  if (props.recoverFromNuxtError) {
    await clearError();
  }

  if (import.meta.client && window.history.length > 1) {
    router.back();
    return;
  }

  await goHome();
}
</script>

<style scoped>
.error-page {
  --error-accent: #2e90fa;
  --error-accent-dark: #194f8a;
  --error-soft: #eaf4ff;
  min-height: 100vh;
  min-height: 100dvh;
  background: #f8f8f8;
  color: #000814;
}

.error-page[data-tone='orange'] {
  --error-accent: #f79009;
  --error-accent-dark: #884f05;
  --error-soft: #fef4e6;
}

.error-page[data-tone='red'] {
  --error-accent: #f04437;
  --error-accent-dark: #84251e;
  --error-soft: #feeceb;
}

.error-page__frame {
  display: flex;
  width: min(100%, 1240px);
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  padding: 0 32px;
  flex-direction: column;
}

.error-page__header {
  display: flex;
  min-height: 88px;
  align-items: center;
  border-bottom: 1px solid #e1e1e1;
}

.error-page__brand {
  border: 0;
  background: transparent;
  color: #000814;
  cursor: pointer;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  padding: 10px 0;
}

.error-page__brand strong {
  color: #1ed760;
  font-weight: 700;
}

.error-page__content {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) 420px;
  align-items: center;
  gap: 72px;
  padding: 56px 0;
}

.error-page__copy {
  max-width: 650px;
}

.error-page__badge {
  display: inline-flex;
  min-height: 36px;
  align-items: center;
  gap: 9px;
  border: 1px solid color-mix(in srgb, var(--error-accent) 32%, white);
  border-radius: 6px;
  background: var(--error-soft);
  color: var(--error-accent-dark);
  font-size: 12px;
  font-weight: 700;
  padding: 7px 11px;
  text-transform: uppercase;
}

.error-page__badge i {
  color: var(--error-accent);
  font-size: 17px;
}

.error-page__code {
  margin-top: 28px;
  color: #000814;
  font-size: 112px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 0.9;
}

.error-page__code span {
  color: #1ed760;
}

.error-page h1 {
  max-width: 620px;
  margin: 26px 0 0;
  color: #000814;
  font-size: 36px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
}

.error-page__description {
  max-width: 590px;
  margin: 14px 0 0;
  color: #697586;
  font-size: 17px;
  line-height: 1.7;
}

.error-page__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 32px;
}

.error-page__button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px solid #d1d4d9;
  border-radius: 7px;
  background: #ffffff;
  color: #333943;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;
  transition:
    border-color 160ms ease,
    background-color 160ms ease,
    color 160ms ease,
    transform 160ms ease;
}

.error-page__button:hover {
  border-color: #9ba3ae;
  background: #f6f6f6;
}

.error-page__button:active {
  transform: translateY(1px);
}

.error-page__button:focus-visible,
.error-page__brand:focus-visible {
  outline: 3px solid rgb(30 215 96 / 24%);
  outline-offset: 3px;
}

.error-page__button.is-primary {
  border-color: #1ed760;
  background: #1ed760;
  color: #000814;
}

.error-page__button.is-primary:hover {
  border-color: #1bc457;
  background: #1bc457;
}

.error-page__button.is-quiet {
  border-color: transparent;
  background: transparent;
  color: #697586;
}

.error-page__button.is-quiet:hover {
  background: #ebebeb;
  color: #333943;
}

.error-page__panel {
  overflow: hidden;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgb(0 8 20 / 8%);
}

.error-page__visual {
  position: relative;
  height: 224px;
  overflow: hidden;
  border-bottom: 1px solid #e1e1e1;
  background: #000814;
}

.error-page__sheet {
  position: absolute;
  top: 34px;
  left: 50%;
  width: 132px;
  height: 164px;
  border: 1px solid rgb(255 255 255 / 18%);
  border-radius: 7px;
  background: #ffffff;
}

.error-page__sheet.is-back {
  opacity: 0.2;
  transform: translateX(-82%) rotate(-7deg);
}

.error-page__sheet.is-middle {
  opacity: 0.35;
  transform: translateX(-22%) rotate(7deg);
}

.error-page__sheet.is-front {
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding: 30px 22px;
  transform: translateX(-50%);
}

.error-page__sheet-line {
  display: block;
  width: 100%;
  height: 5px;
  border-radius: 2px;
  background: #d1d4d9;
}

.error-page__sheet-line.is-short {
  width: 62%;
}

.error-page__visual-mark {
  position: absolute;
  z-index: 3;
  right: 72px;
  bottom: 24px;
  display: grid;
  width: 56px;
  height: 56px;
  place-items: center;
  border: 5px solid #000814;
  border-radius: 8px;
  background: var(--error-accent);
  color: #ffffff;
  font-size: 26px;
}

.error-page__visual-code {
  position: absolute;
  top: 22px;
  right: 24px;
  color: rgb(255 255 255 / 32%);
  font-size: 18px;
  font-weight: 700;
}

.error-page__panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px 18px;
}

.error-page__panel-heading span {
  color: #697586;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.error-page__panel-heading strong {
  color: #000814;
  font-size: 16px;
  font-weight: 700;
}

.error-page__facts {
  margin: 0;
  padding: 0 24px;
}

.error-page__facts > div {
  display: grid;
  grid-template-columns: 116px minmax(0, 1fr);
  gap: 16px;
  border-top: 1px solid #ebebeb;
  padding: 17px 0;
}

.error-page__facts dt {
  color: #333943;
  font-size: 13px;
  font-weight: 700;
}

.error-page__facts dd {
  margin: 0;
  color: #697586;
  font-size: 13px;
  line-height: 1.55;
}

.error-page__support {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  border-top: 1px solid #ebebeb;
  background: #f8f8f8;
  color: #697586;
  font-size: 12px;
  line-height: 1.5;
  padding: 16px 24px;
}

.error-page__support i {
  flex: 0 0 auto;
  color: var(--error-accent);
  font-size: 17px;
  margin-top: 1px;
}

.error-page__button .icon-arrow-back.is-rtl {
  transform: rotate(180deg);
}

@media (max-width: 960px) {
  .error-page__content {
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 40px;
  }

  .error-page__code {
    font-size: 92px;
  }

  .error-page h1 {
    font-size: 31px;
  }
}

@media (max-width: 760px) {
  .error-page__frame {
    padding: 0 22px;
  }

  .error-page__header {
    min-height: 76px;
  }

  .error-page__content {
    grid-template-columns: 1fr;
    align-items: start;
    gap: 36px;
    padding: 44px 0;
  }

  .error-page__copy {
    max-width: none;
  }

  .error-page__code {
    font-size: 76px;
  }

  .error-page h1 {
    font-size: 28px;
  }

  .error-page__description {
    font-size: 16px;
  }

  .error-page__panel {
    width: 100%;
  }
}

@media (max-width: 460px) {
  .error-page__frame {
    padding: 0 18px;
  }

  .error-page__brand {
    font-size: 19px;
  }

  .error-page__content {
    padding: 36px 0;
  }

  .error-page__code {
    margin-top: 24px;
    font-size: 68px;
  }

  .error-page h1 {
    margin-top: 20px;
    font-size: 25px;
  }

  .error-page__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .error-page__button.is-quiet {
    grid-column: 1 / -1;
  }

  .error-page__visual {
    height: 208px;
  }

  .error-page__visual-mark {
    right: 46px;
  }

  .error-page__facts > div {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .error-page__button {
    transition: none;
  }
}
</style>
